import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FileText, Users, ArrowRight, BookOpen, Plus, Sparkles } from "lucide-react";

function StatusPill({ status }) {
  const done = status === "completed";
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
      done ? "bg-[#F2E9FA] text-[#3F3A44]" : "bg-[#FEF6E8] text-[#3B0764]"}`}>
      {done ? "Completed" : "Awaiting response"}
    </span>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/start"); return; }
    api.me().then(setMe).catch(() => {});
  }, [user, navigate]);

  if (!me) return <div className="max-w-4xl mx-auto px-5 py-24 text-center text-[#918780]">Loading…</div>;

  const latest = me.assessments[0];

  return (
    <div className="max-w-4xl mx-auto px-5 py-12 space-y-8">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2625]">
          Hello, {me.user.name.split(" ")[0]}
        </h1>
        <p className="text-[#6E655F] mt-1">Your personal space for insight and connection.</p>
      </div>

      {!latest ? (
        <div className="rounded-3xl bg-[#6B21A8] text-white p-8 text-center">
          <Sparkles className="mx-auto mb-3" size={26} />
          <h3 className="font-display text-2xl font-bold mb-2">Take your assessment</h3>
          <p className="text-[#F3E3FC] mb-5">120 gentle questions, about 12 minutes.</p>
          <Link to={me.user.subscribed ? "/assessment" : "/checkout"} data-testid="dash-take-assessment"
            className="inline-flex items-center gap-2 bg-white text-[#6B21A8] font-semibold py-3 px-7 rounded-xl">
            Begin now <ArrowRight size={18} />
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="bg-white border border-[#E8E1D9] rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 text-[#6B21A8] font-semibold mb-3"><FileText size={18} /> Your report</div>
            <p className="font-display text-xl font-semibold text-[#2B2625] mb-1">{latest.headline || latest.headline_type}</p>
            <p className="text-sm text-[#918780] mb-4">{latest.headline_type}</p>
            <Link to={`/report/${latest.id}`} data-testid="dash-view-report"
              className="inline-flex items-center gap-1.5 text-[#6B21A8] font-semibold text-sm hover:gap-2.5 transition-all">
              View report <ArrowRight size={16} />
            </Link>
          </div>
          <div className="bg-[#3F3A44] text-white rounded-2xl p-6 shadow-sm flex flex-col">
            <div className="flex items-center gap-2 font-semibold mb-3"><Plus size={18} /> Invite someone</div>
            <p className="text-[#E4D6F5] text-sm mb-4 flex-1">
              {me.user.free_invite_used ? "Add another person to explore how you connect." : "Your first invite is included — free."}
            </p>
            <Link to="/invite" data-testid="dash-invite"
              className="inline-flex items-center gap-1.5 bg-[#7C3AED] text-[#2B2625] font-semibold text-sm py-2.5 px-5 rounded-xl w-fit hover:bg-[#5B21B6] transition-colors">
              Send invite <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}

      {/* Invites */}
      {me.invites.length > 0 && (
        <div>
          <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-3 flex items-center gap-2">
            <Users size={18} /> People you've invited
          </h3>
          <div className="space-y-3">
            {me.invites.map((inv) => (
              <div key={inv.token} className="bg-white border border-[#E8E1D9] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#2B2625]">{inv.invitee_name || inv.invitee_email}</p>
                  <p className="text-sm text-[#918780] capitalize">{inv.relationship}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill status={inv.status} />
                  {inv.pair_id && (
                    <Link to={`/combination/${inv.pair_id}`} data-testid={`dash-pair-${inv.pair_id}`}
                      className="text-[#6B21A8] font-semibold text-sm hover:underline">View</Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pairs where I'm the invitee */}
      {me.pairs.length > 0 && (
        <div>
          <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-3 flex items-center gap-2">
            <Sparkles size={18} /> Your shared reports
          </h3>
          <div className="space-y-3">
            {me.pairs.map((p) => (
              <Link key={p.id} to={`/combination/${p.id}`} data-testid={`dash-shared-${p.id}`}
                className="block bg-white border border-[#E8E1D9] rounded-xl p-4 flex items-center justify-between hover:border-[#6B21A8]/40 transition-colors">
                <div>
                  <p className="font-semibold text-[#2B2625]">{p.name1} & {p.name2}</p>
                  <p className="text-sm text-[#918780] capitalize">{p.relationship}</p>
                </div>
                <span className="font-display text-2xl font-bold text-[#3F3A44]">{p.compat.overall}%</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Link to="/teams" data-testid="dash-teams"
        className="block rounded-2xl border border-[#E8E1D9] bg-white p-6 flex items-center justify-between hover:shadow-sm transition">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-[#F2E9FA] text-[#3F3A44] grid place-items-center"><Users size={20} /></span>
          <div>
            <p className="font-semibold text-[#2B2625]">Team snapshots</p>
            <p className="text-sm text-[#918780]">Invite your team & see how you work best together</p>
          </div>
        </div>
        <ArrowRight className="text-[#6B21A8]" size={20} />
      </Link>

      <Link to="/ebook" data-testid="dash-ebook"
        className="block rounded-2xl border border-[#E8E1D9] bg-[#F3EDE4] p-6 flex items-center justify-between hover:shadow-sm transition">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-white text-[#6B21A8] grid place-items-center"><BookOpen size={20} /></span>
          <div>
            <p className="font-semibold text-[#2B2625]">Read the RelateMetrics eBook</p>
            <p className="text-sm text-[#918780]">Self-awareness, emotional intelligence & better relationships</p>
          </div>
        </div>
        <ArrowRight className="text-[#6B21A8]" size={20} />
      </Link>
    </div>
  );
}
